import { QrCodeIcon } from "@heroicons/react/16/solid";
import { Button } from "@/core/components/ui/button";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { useGetBusiness } from "@/business/query";
import { useBusiness } from "@/core/business";
import { cn } from "@/core/utils/utils";
import { getCustomerNameOrPhone } from "@/customer/utils";
import InfoPair from "./info-pair";
import { NameAndDate, NameAndDateSkeleton } from "./name-and-date";
import type { ReferralContentProps } from "./referralContent";

interface NewReferralCardContentProps extends ReferralContentProps {
  onAccept: () => void;
  onLost?: () => void;
  onMoveToPaid?: () => void;
  onDelete?: () => void;
}

export const NewReferralCardSkeleton = () => (
  <Stack className="p-4">
    <NameAndDateSkeleton showDate />
    <Stack gap="sm">
      <Skeleton className="h-4 w-32" /> {/* Services */}
      <Skeleton className="h-4 w-48" /> {/* Referred by */}
    </Stack>
    <Skeleton className="h-10 w-full rounded-full" /> {/* Accept button */}
  </Stack>
);

export const NewReferralCardContent = ({
  referral,
  onAccept,
  onLost,
  onMoveToPaid,
  onDelete,
}: NewReferralCardContentProps) => {
  const { businessId } = useBusiness();
  const { data: business } = useGetBusiness(businessId);

  const attributedUserName =
    referral.advocate.signup_source?.display_identification;
  return (
    <Stack fullWidth>
      {attributedUserName && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Stack
              direction="row"
              gap="sm"
              align="center"
              className="rounded-t-2xl bg-bg-secondary px-4 py-2"
              fullWidth
            >
              <QrCodeIcon className="h-4 w-4 text-tertiary-text" />
              <Typography variant="captionW1">{attributedUserName}</Typography>
            </Stack>
          </TooltipTrigger>
          <TooltipContent>
            {business?.name} team member that signed up the advocate that sent
            this referral
          </TooltipContent>
        </Tooltip>
      )}
      <Stack
        className={cn("px-4 pb-4", !attributedUserName && "pt-4")}
        fullWidth
      >
        <NameAndDate
          referral={referral}
          onLost={onLost}
          onMoveToPaid={onMoveToPaid}
          onDelete={onDelete}
        />
        <Stack gap="sm">
          <InfoPair
            label="Services"
            info={referral.service_needed || "No Services"}
          />
          <InfoPair
            label="Referred by"
            info={getCustomerNameOrPhone(referral.advocate)}
          />
        </Stack>
        <Button
          size="sm"
          fullWidth
          onClick={(e) => {
            e.stopPropagation();
            onAccept();
          }}
        >
          Accept
        </Button>
      </Stack>
    </Stack>
  );
};
